import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { Layout } from "./components/layout/Layout";

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Public Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import Initiatives from "./pages/Initiatives";
import InitiativeDetail from "./pages/InitiativeDetail";
import Media from "./pages/Media";
import MediaDetail from "./pages/MediaDetail";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";

// Admin Pages
import AdminDashboard, { AdminLayout } from "./pages/admin/AdminDashboard";
import AdminArticles from "./pages/admin/AdminArticles";
import ArticleEditor from "./pages/admin/ArticleEditor";
import AdminInitiatives from "./pages/admin/AdminInitiatives";
import InitiativeEditor from "./pages/admin/InitiativeEditor";
import AdminMedia from "./pages/admin/AdminMedia";
import MediaEditor from "./pages/admin/MediaEditor";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPending from "./pages/admin/AdminPending";
import AdminNotifications from "./pages/admin/AdminNotifications";

// Author Pages
import AuthorDashboard, { AuthorLayout } from "./pages/author/AuthorDashboard";
import AuthorArticles from "./pages/author/AuthorArticles";
import AuthorArticleEditor from "./pages/author/AuthorArticleEditor";

// Router wrapper to detect session_id in URL hash
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - do this synchronously during render
  // This prevents race conditions by processing new session_id FIRST before checking existing session_token
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes with Layout */}
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/articles" element={<Layout><Articles /></Layout>} />
      <Route path="/articles/:id" element={<Layout><ArticleDetail /></Layout>} />
      <Route path="/initiatives" element={<Layout><Initiatives /></Layout>} />
      <Route path="/initiatives/:id" element={<Layout><InitiativeDetail /></Layout>} />
      <Route path="/media" element={<Layout><Media /></Layout>} />
      <Route path="/media/:id" element={<Layout><MediaDetail /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      
      {/* Auth Routes (No Layout) */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
      <Route path="/admin/pending" element={<AdminLayout><AdminPending /></AdminLayout>} />
      <Route path="/admin/notifications" element={<AdminLayout><AdminNotifications /></AdminLayout>} />
      <Route path="/admin/articles" element={<AdminLayout><AdminArticles /></AdminLayout>} />
      <Route path="/admin/articles/new" element={<AdminLayout><ArticleEditor /></AdminLayout>} />
      <Route path="/admin/articles/:id/edit" element={<AdminLayout><ArticleEditor /></AdminLayout>} />
      <Route path="/admin/initiatives" element={<AdminLayout><AdminInitiatives /></AdminLayout>} />
      <Route path="/admin/initiatives/new" element={<AdminLayout><InitiativeEditor /></AdminLayout>} />
      <Route path="/admin/initiatives/:id/edit" element={<AdminLayout><InitiativeEditor /></AdminLayout>} />
      <Route path="/admin/media" element={<AdminLayout><AdminMedia /></AdminLayout>} />
      <Route path="/admin/media/new" element={<AdminLayout><MediaEditor /></AdminLayout>} />
      <Route path="/admin/media/:id/edit" element={<AdminLayout><MediaEditor /></AdminLayout>} />
      <Route path="/admin/contacts" element={<AdminLayout><AdminContacts /></AdminLayout>} />
      <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
      
      {/* Author Routes */}
      <Route path="/author" element={<AuthorLayout><AuthorDashboard /></AuthorLayout>} />
      <Route path="/author/articles" element={<AuthorLayout><AuthorArticles /></AuthorLayout>} />
      <Route path="/author/articles/new" element={<AuthorLayout><AuthorArticleEditor /></AuthorLayout>} />
      <Route path="/author/articles/:id/edit" element={<AuthorLayout><AuthorArticleEditor /></AuthorLayout>} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
